import React, { useState, useEffect } from 'react';
import { useApi } from '../Api';
import { useAuth0 } from '@auth0/auth0-react';
import { useBillComCheck } from '../hooks/useBillComCheck';
import { Family } from '../types/Messaging';
import { PageMasthead } from '../components/PageMasthead';
import { FormSelect } from '../controls/FormSelect';

interface Invoice {
    id: string;
    invoiceNumber?: string;
    invoiceDate?: string;
    dueDate?: string;
    customerId: string;
    totalAmount?: number;
    dueAmount?: number;
    status?: string;
    createdTime?: string;
    updatedTime?: string;
    billComUrl?: string;
}

const InvoicesPage: React.FC = () => {
    const api = useApi();
    const { getAccessTokenSilently } = useAuth0();
    const hasBillComAccess = useBillComCheck();
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingInvoices, setLoadingInvoices] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [families, setFamilies] = useState<Family[]>([]);
    const [selectedFamilyId, setSelectedFamilyId] = useState<number | "">("");

    useEffect(() => {
        const fetchUserData = async () => {
            setLoading(true);
            // Don't fetch if user doesn't have Bill.com access
            if (hasBillComAccess === false) {
                setError('You do not have access to Bill.com invoices. Please contact a concierge to set up your Bill.com integration.');
                setLoading(false);
                return;
            }

            // Don't fetch if we're still checking access
            if (hasBillComAccess === null) {
                return;
            }

            try {
                const familiesResponse = await api.get('/api/clientfamily/getfamilies');
                setFamilies(familiesResponse.data.families || []);
            } catch (err) {
                setError('Failed to fetch invoices');
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, [api, getAccessTokenSilently, hasBillComAccess]);

    const loadInvoices = async (familyId: number) => {
        setLoadingInvoices(true);
        if (!familyId) {
            setError('Please select a family.');
            setLoadingInvoices(false);
            return;
        }
        try {
            setError(null);
            const response = await api.get(`/api/invoice/getInvoices?familyId=${familyId}`);
            setInvoices(response.data.invoices.results || []);
        } catch (err) {
            console.error('Error creating conversation:', err);
            setError('Failed to create conversation');
        } finally {
            setLoadingInvoices(false);
        }
    }

    if (loading || hasBillComAccess === null) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    // Show access denied message if user doesn't have Bill.com access
    if (hasBillComAccess === false) {
        return (
            <div className="max-w-4xl mx-auto p-6 space-y-6">
                <div className="bg-white rounded-xl shadow-md px-6 py-8">
                    <div className="text-center">
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
                            <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Bill.com Access Required</h1>
                        <p className="text-gray-600 mb-6">
                            You do not have access to Bill.com invoices. Please contact a concierge to set up your Bill.com integration.
                        </p>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                            <p className="text-sm text-yellow-800">
                                <strong>Note:</strong> Bill.com integration is required to view and manage invoices. 
                                Once your account is properly configured, you'll be able to access your invoices here.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto p-6 space-y-6">
            {/* Header Card */}
            <div className="bg-white rounded-xl shadow-md px-6 py-8">
                <PageMasthead eyebrow="Billing" title="Bill.com Invoices" subtitle="View and manage your invoices from Bill.com" />
                <FormSelect
                    btnTestId="family-dropdown"
                    label="Family"
                    placeholder="Pick a family"
                    value={selectedFamilyId === "" ? null : selectedFamilyId}
                    options={families.map(fam => ({ value: fam.id, label: fam.name }))}
                    onChange={(val) => {
                        if (val === null) return;
                        setSelectedFamilyId(val as number);
                        loadInvoices(val as number);
                    }}
                />
            </div>

            {/* Error Card */}
            {error && (
                <div className="bg-white rounded-xl shadow-md p-6">
                    <div className="alert-danger">
                        {error}
                    </div>
                </div>
            )}

            {/* Loading Card */}
            {loadingInvoices && (
                <div className="flex justify-center items-center min-h-screen">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
                </div>
            )}

            {/* Invoices Card */}
            <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">
                        Invoices {invoices.length > 0 && `(${invoices.length})`}
                    </h2>
                    {invoices.length > 0 && (
                        <>
                            <span className="mx-3 text-gray-400">|</span>
                            <div className="text-lg font-bold text-gray-900">
                                Total: <span className="dag-amount">{invoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
                            </div>
                        </>
                    )}
                </div>

                {invoices.length === 0 ? (
                    <div className="text-center py-8">
                        <div className="text-gray-500 mb-4">
                            {error ? 'Unable to load invoices' : 'No invoices found for this customer'}
                        </div>
                        {!error && (
                            <p className="text-sm text-gray-400">
                                Invoices will appear here once your Bill.com integration is set up
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {invoices.map((invoice) => (
                            <div key={invoice.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-4 mb-2">
                                            <h3 className="text-lg font-medium text-gray-900">
                                                {invoice.invoiceNumber || 'Invoice #' + invoice.id}
                                            </h3>
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                invoice.status === 'Unpaid' ? 'bg-red-100 text-red-800' :
                                                invoice.status === 'Paid' ? 'bg-green-100 text-green-800' :
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                                {invoice.status || 'Unknown'}
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                                            <div>
                                                <span className="font-medium">Invoice Date:</span>
                                                <div>{invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString() : 'N/A'}</div>
                                            </div>
                                            <div>
                                                <span className="font-medium">Due Date:</span>
                                                <div>{invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'N/A'}</div>
                                            </div>
                                            <div>
                                                <span className="font-medium">Total Amount:</span>
                                                <div className="font-semibold text-gray-900 dag-amount">
                                                    {invoice.totalAmount ? invoice.totalAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'N/A'}
                                                </div>
                                            </div>
                                            <div>
                                                <span className="font-medium">Due Amount:</span>
                                                <div className="font-semibold text-gray-900 dag-amount">
                                                    {invoice.dueAmount ? invoice.dueAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'N/A'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="ml-4">
                                        {invoice.billComUrl ? (
                                            <a
                                                href={invoice.billComUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="link"
                                            >
                                                View in Bill.com
                                            </a>
                                        ) : (
                                            <span className="text-gray-400 text-sm">No link available</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default InvoicesPage;